
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Check, ArrowRight } from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import SeatsCounter from './SeatsCounter';
import { supabase } from '@/integrations/supabase/client';

const OptInForm = () => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{firstName?: string; email?: string; phone?: string}>({});
  const { toast } = useToast();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors: {firstName?: string; email?: string; phone?: string} = {};
    let isValid = true;

    if (!firstName.trim()) {
      newErrors.firstName = "Your name is required";
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
      isValid = false;
    }

    if (!phone.trim()) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!/^\+?[0-9\s-()]{7,}$/.test(phone)) {
      newErrors.phone = "Phone number is invalid";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      console.log('Submitting form with data:', { firstName, email, phone });
      
      try {
        console.log('Attempting to insert data into Supabase contacts table');
        
        // First check if the email already exists
        const { data: existingContact } = await supabase
          .from('contacts')
          .select('email')
          .eq('email', email);
          
        if (existingContact && existingContact.length > 0) {
          // Email already exists - but we can still proceed to welcome page
          console.log('Email already exists, proceeding to welcome page');
          
          toast({
            title: "Welcome back!",
            description: "You've already registered for the webinar.",
            variant: "default",
          });
          
          // Store user data in session storage for welcome page
          sessionStorage.setItem('userData', JSON.stringify({
            firstName,
            email,
            phone
          }));
          
          navigate('/welcome');
          return;
        }
        
        // If email doesn't exist, proceed with insertion
        const insertResponse = await supabase
          .from('contacts')
          .insert({
            first_name: firstName, 
            email, 
            phone 
          });
        
        console.log('Insert response:', insertResponse);
        
        if (insertResponse.error) {
          console.error("Supabase insert error details:", {
            message: insertResponse.error.message,
            details: insertResponse.error.details,
            hint: insertResponse.error.hint,
            code: insertResponse.error.code
          });
          
          if (insertResponse.error.code === '23505') {
            // This is a duplicate key error (email already exists)
            toast({
              title: "Welcome back!",
              description: "You've already registered for the webinar.",
              variant: "default",
            });
            
            // Store user data in session storage for welcome page
            sessionStorage.setItem('userData', JSON.stringify({
              firstName,
              email,
              phone
            }));
            
            navigate('/welcome');
          } else {
            toast({
              title: "Error",
              description: `Database error: ${insertResponse.error.message}`,
              variant: "destructive",
            });
          }
        } else {
          console.log('Contact saved successfully');
          
          // Send email notification
          try {
            const emailResponse = await fetch('https://ieukprkpvqjjaoigjydc.supabase.co/functions/v1/send-contact-email', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                firstName,
                email,
                phone
              }),
            });

            if (!emailResponse.ok) {
              console.error('Failed to send email notification');
            } else {
              console.log('Email notification sent successfully');
            }
          } catch (emailError) {
            console.error('Error sending email notification:', emailError);
          }

          // Store user data in session storage for welcome page
          sessionStorage.setItem('userData', JSON.stringify({
            firstName,
            email,
            phone
          }));
          
          toast({
            title: "Registration successful!",
            description: "You're all set for the AI Kids Academy webinar.",
            variant: "default",
          });
          
          navigate('/welcome');
        }
      } catch (error) {
        console.error("Unexpected error during form submission:", error);
        toast({
          title: "Error",
          description: "Something went wrong with your submission. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Update the webinar date to Thursday, 29th May 2025 at 3:00 PM UK time
  const webinarDate = new Date('May 29, 2025 15:00:00 GMT+0100');

  return (
    <div className="w-full bg-white p-6 rounded-xl shadow-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            Your Name
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-brand-300`}
            placeholder="Your full name"
          />
          {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-brand-300`}
            placeholder="you@example.com"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-brand-300`}
            placeholder="Your phone number"
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>
        
        <button
          type="submit"
          disabled={isSubmitting}
          className="cta-button mt-4 w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 py-4 rounded-lg text-white text-lg font-bold transition-colors"
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              <span>Processing...</span>
            </div>
          ) : (
            <>
              <Check className="w-5 h-5" /> 
              <span>Yes, I Want My Child Ahead of the Curve</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <SeatsCounter />
        
        <CountdownTimer 
          targetDate={webinarDate} 
          label="🕒 Webinar Starts In:" 
        />
      </form>
    </div>
  );
};

export default OptInForm;
